import { inject } from "inversify";
import { CaseForUpdate, CaseServiceInterface, Helpers, NamedBindings } from "@dandelion/core";
import { ReadmissionStorageInterface } from "../interfaces/ReadmissionStorageInterface";
import { ReadmissionHistory } from "../services/ReadmissionManagerRunner";
import RegistryForHash from "../interfaces/Registry";
import { Crypt } from "../utils/Crypt";
import { ReadmissionService } from "../services/ReadmissionService";

export default class ReadmissionHashChange {

  constructor(@inject(NamedBindings.CaseService) protected caseService: CaseServiceInterface, @inject("ReadmissionStorage") protected readmissionStorage: ReadmissionStorageInterface) { }

  async handleHashChangeIfNeeded(
    model: any,
    previousModel: any,
    currentCaseId: string,
    centreCode: string,
    // This dialog function can be passed for mocking the response in tests
    dialogForAskingIfWeShouldChangeAllLinkedAdmissions: () => Promise<boolean> = () => {
      return new Promise((resolve) => {
        const userConfirmed = confirm("Cambiare i dati anche di tutte le eventuali riammissioni?");
        resolve(userConfirmed);
      });
    }
  ) {
    const currentPersonalData = model.core?.personalData;
    const previousPersonalData = previousModel.core?.personalData;
    const admissionDate = Helpers.getObjByPath(ReadmissionService.VARIABLE_FOR_READMISSION_DATE, model);
    const prevAdmissionDate = Helpers.getObjByPath(ReadmissionService.VARIABLE_FOR_READMISSION_DATE, previousModel);

    const currentVariablesToHash: RegistryForHash =
    {
      name: currentPersonalData.name,
      surname: currentPersonalData.surname,
      birthDate: currentPersonalData.birthDate,
      centreCode: centreCode
    };
    const previousVariablesToHash: RegistryForHash =
    {
      name: previousPersonalData.name,
      surname: previousPersonalData.surname,
      birthDate: previousPersonalData.birthDate,
      centreCode: centreCode
    };
    const newHash = await Crypt.generateHash(currentVariablesToHash);
    const previousHash = await Crypt.generateHash(previousVariablesToHash);

    // Manage case of hash changed (some personal data changed)
    if (newHash != previousHash) {
      model.hashForReadmission = newHash;
      const shouldChangeAllTheLinkedAdmissions = await dialogForAskingIfWeShouldChangeAllLinkedAdmissions();
      if (shouldChangeAllTheLinkedAdmissions) {
        await this.processHashChangeForAllTheChain(newHash, previousHash, currentCaseId, currentPersonalData, admissionDate);
      } else {
        await this.separateAdmissionFromChain(newHash, previousHash, currentCaseId, previousModel, admissionDate, centreCode);
      }
    }

    // Other checks are made after hash changes so the admission history is already in the correct place to be evntually modified again

    // Manage case of changed admissionDate
    if (admissionDate != prevAdmissionDate) {
      const currentAdmissionsChains = await this.readmissionStorage.getPrevAdmissionsHypothesis(newHash);
      if (currentAdmissionsChains) {
        const admission = this.findCaseInChains(currentAdmissionsChains, currentCaseId);
        if (admission) {
          admission.admissionDate = admissionDate;
          this.readmissionStorage.save(newHash, currentAdmissionsChains);
        }
      }
    }

    // Manage case of patient dead
    if (this.isPatientDead(model)) {
      const currentAdmissionsChains = await this.readmissionStorage.getPrevAdmissionsHypothesis(newHash);
      if (currentAdmissionsChains) {
        this.readmissionStorage.delete(newHash, this.findChainIndexForCase(currentAdmissionsChains, currentCaseId));
      }
    }
  }

  async removeCaseFromChains(caseIdToRemove: string, model: any, centreCode: string) {
    const variablesToHash: RegistryForHash =
    {
      name: model?.core?.personalData?.name,
      surname: model?.core?.personalData?.surname,
      birthDate: model?.core?.personalData?.birthDate,
      centreCode: centreCode
    };
    const hash = await Crypt.generateHash(variablesToHash);
    const currentAdmissionsChains = await this.readmissionStorage.getPrevAdmissionsHypothesis(hash);
    if (!currentAdmissionsChains || currentAdmissionsChains.length === 0) {
      console.log(`Hash: ${hash} already removed from readmission chains`);
      return;
    }
    const updatedAdmissionChains = currentAdmissionsChains
      .map(chain => this.getChainWithoutAdmission(chain, caseIdToRemove))
      // Remove possible empty chains (if the admission was the only one in the chain)
      .filter(chain => chain !== undefined);
    await this.readmissionStorage.save(hash, updatedAdmissionChains);
  }

  protected getChainWithoutAdmission(history: ReadmissionHistory | undefined, caseIdToRemove: string): ReadmissionHistory | undefined {
    if (!history) return undefined;

    if (history.caseId === caseIdToRemove) {
      return history.prevAdmission; // Remove the node by linking the next to the previous
    }

    // Otherwise continue the search...
    history.prevAdmission = this.getChainWithoutAdmission(history.prevAdmission, caseIdToRemove);
    return history; // .. and keep the same structure as before
  }

  protected isPatientDead(model: Record<string, any>) {
    return model.core?.icuOutcomePage?.icuOutcome == 'icuOutCodification.dead'
      || model.core?.outcome?.hospOutcomeIT == 'hospOutCodificationIT.hospDead'
      || model.core?.outcome?.hospOutcomeLatest == 'hospOutCodification.hospDead'
  }

  protected async processHashChangeForAllTheChain(newHash: string, previousHash: string, caseId: string, currentData: any, admissionDate: string) {
    const readmissionHistories = await this.readmissionStorage.getPrevAdmissionsHypothesis(previousHash);

    if (readmissionHistories) {
      const historyIndex = this.findChainIndexForCase(readmissionHistories, caseId);
      if (historyIndex !== -1) {
        const history = readmissionHistories[historyIndex];
        await this.updateCaseData(history, caseId, currentData, newHash);
        await this.readmissionStorage.save(newHash, [history]);
        await this.readmissionStorage.delete(previousHash, historyIndex);
      } else {
        const historyToBeSaved: ReadmissionHistory = {
          admissionDate,
          caseId,
          prevAdmission: undefined,
        }
        await this.readmissionStorage.save(newHash, [historyToBeSaved]);
      }
    }
  }

  protected async separateAdmissionFromChain(
    newHash: string,
    previousHash: string,
    caseId: string,
    previousModel: any,
    admissionDate: string,
    centreCode: string,
  ) {
    let historyToBeSaved: ReadmissionHistory = {
      admissionDate,
      caseId,
      prevAdmission: undefined,
    }

    const readmissionHistories = await this.readmissionStorage.getPrevAdmissionsHypothesis(previousHash);
    if (readmissionHistories) {
      const history = this.findCaseInChains(readmissionHistories, caseId);
      if (history) {
        historyToBeSaved = { ...history, prevAdmission: undefined };
      }

      // We need previousModel in order to remove it from the chain where it was before the changes
      await this.removeCaseFromChains(caseId, previousModel, centreCode);
    }

    const readmissionHistoriesForNewHash = await this.readmissionStorage.getPrevAdmissionsHypothesis(newHash);
    await this.readmissionStorage.save(newHash, [ ...readmissionHistoriesForNewHash ?? [], historyToBeSaved]);
  }

  protected async updateCaseData(
    history: ReadmissionHistory,
    currentCaseId: string,
    currentData: any,
    newHash: string,
    deep = true
  ) {
    // We avoid updating the case id currently opened, as we are in beforeSaving
    if (history.caseId != currentCaseId) {
      const model = (await this.caseService.getDataFor(history.caseId)).data;
      const metaData = await this.caseService.getCaseMetaDataFor(history.caseId);
      Helpers.setObjByPath("core.personalData.name", model, currentData.name);
      Helpers.setObjByPath("core.personalData.surname", model, currentData.surname);
      Helpers.setObjByPath("core.personalData.birthDate", model, currentData.birthDate);
      // This is commented because it helps to reconstruct what happened. We still have hashForReadmission to cluster cases
      // Helpers.setObjByPath("previousAdmissionId", model, history.prevAdmission?.caseId);
      Helpers.setObjByPath("hashForReadmission", model, newHash);
      await this.caseService.update(new CaseForUpdate(history.caseId, model, metaData));
    }

    // Process previous admission recursively if it exists
    if (deep && history.prevAdmission) {
      await this.updateCaseData(history.prevAdmission, currentCaseId, currentData, newHash);
    }
  }

  protected findCaseInChains(
    chains: ReadmissionHistory[],
    caseId: string
  ): ReadmissionHistory | undefined {
    return chains.map(history => this.findCaseInChain(history, caseId)).find(h => h !== undefined);
  }

  protected findCaseInChain(history: ReadmissionHistory | undefined, caseId: string): ReadmissionHistory | undefined {
    if (!history) return undefined;
    if (history.caseId === caseId) return history;
    return this.findCaseInChain(history.prevAdmission, caseId);
  }

  protected findChainIndexForCase(
    chains: ReadmissionHistory[],
    caseId: string
  ): number {
    return chains.findIndex(history => this.isInChain(history, caseId)) ?? -1;
  }

  // Helper function to search recursively within the single history chain
  protected isInChain(history: ReadmissionHistory | undefined, caseId: string): boolean {
    if (!history) return false;
    if (history.caseId === caseId) return true;
    return this.isInChain(history.prevAdmission, caseId);
  }
}
